import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Card, Form, Spinner } from 'react-bootstrap';
import moment from 'moment';
import { toast } from 'sonner';
import SlidingSideBar from '../SlidingSideBar/SlidingSideBar';
import { BILLING_NOTE_ALLOWED_ORDER_STATUSES } from '../../helpers/constants';
import { createBillingNote, fetchBillingNotes } from '../../helpers/orderNotes';
import { Plus } from 'lucide-react';
import ClientViewConfirmModal from './ClientViewConfirmModal';

const BillingNotesSheet = ({
  visible,
  onClose,
  orderId,
  orderStatus,
  onNoteAdded,
  fileNo,
  invoiceNumber,
  organizationName,
  propertyAddress,
  noteSource = 'Statement',
  requireClientViewConfirm = false,
  staffName,
  clientName,
  viewAsOrganizationId
}) => {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [text, setText] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const notesScrollRef = useRef(null);

  const canAddNote = BILLING_NOTE_ALLOWED_ORDER_STATUSES.includes(orderStatus);

  const loadNotes = useCallback(async () => {
    if (!orderId) return;

    setLoading(true);
    const { response, error } = await fetchBillingNotes(orderId, { viewAsOrganizationId });

    if (error) {
      toast.error(error);
      setNotes([]);
    } else {
      setNotes(Array.isArray(response) ? response : []);
    }

    setLoading(false);
  }, [orderId, viewAsOrganizationId]);

  useEffect(() => {
    if (!visible || !orderId) return;

    setText('');
    loadNotes();
  }, [visible, orderId, loadNotes]);

  const handleClose = () => {
    if (submitting) return;
    onClose();
  };

  const performAdd = async () => {
    const trimmed = text.trim();
    if (!orderId || !trimmed || submitting || !canAddNote) return;

    setSubmitting(true);
    const { response, error } = await createBillingNote({
      orderId,
      text: trimmed,
      source: noteSource,
      viewAsOrganizationId
    });
    setSubmitting(false);

    if (error) {
      toast.error(error);
      return;
    }

    setText('');
    toast.success('Billing note added');
    onNoteAdded?.(response);
    onClose();
  };

  const handleAdd = async () => {
    const trimmed = text.trim();
    if (!orderId || !trimmed || submitting || !canAddNote) return;

    if (requireClientViewConfirm) {
      setShowConfirm(true);
      return;
    }

    await performAdd();
  };

  useEffect(() => {
    if (loading || notes.length === 0) return;

    const scrollEl = notesScrollRef.current;
    if (!scrollEl) return;

    requestAnimationFrame(() => {
      if (scrollEl.scrollHeight > scrollEl.clientHeight) {
        scrollEl.scrollTo({ top: scrollEl.scrollHeight, behavior: 'smooth' });
      }
    });
  }, [loading, notes]);

  const latestNoteId = useMemo(
    () =>
      notes.reduce((latest, note) => {
        if (!note.createdAt) return latest;
        if (!latest?.createdAt || moment(note.createdAt).isAfter(latest.createdAt)) {
          return note;
        }
        return latest;
      }, null)?._id ?? null,
    [notes]
  );

  return (
    <SlidingSideBar
      style={{ zIndex: 99999999, width: 450, left: 'unset', right: 0 }}
      visible={visible}
      onClose={handleClose}
      title="Billing Notes"
      contentStyle={{ height: '100%', boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)' }}
    >
      <div className="d-flex flex-column h-100">
        <div className="mb-2 pb-3 border-bottom p-2">
          <p className="mb-0 xsmallFont text-dark">
            <span className="fw-bold">{fileNo || '—'}</span>
            {' - Invoice #'}
            {invoiceNumber || '—'}
            {organizationName ? ` - ${organizationName}` : ''}
          </p>
          <p className="mb-0 xsmallFont text-muted">{propertyAddress || '—'}</p>
        </div>

        <div ref={notesScrollRef} className="flex-grow-1 overflow-auto scrollbar-thin px-2 pt-1">
          {loading ? (
            <div className="d-flex align-items-center justify-content-center text-muted xsmallFont py-3">
              <Spinner animation="border" size="sm" className="mr-2" />
              Loading billing notes...
            </div>
          ) : notes.length === 0 ? (
            <p className="text-muted xsmallFont mb-0">No billing notes yet.</p>
          ) : (
            notes.map(note => {
              const isLatest = note._id === latestNoteId;
              const borderColor = isLatest ? 'var(--info)' : 'var(--light-grey-2)';

              return (
                <Card
                  key={note._id}
                  className="p-3 mb-2 w-100 border-none bg-white"
                  style={{ borderLeft: `3px solid ${borderColor}` }}
                >
                  <Card.Body className="p-0">
                    <div className="d-flex align-items-center justify-content-between flex-wrap mb-2">
                      <span className="fw-bold xsmallFont text-dark mr-2">{note.author || 'Unknown'}</span>
                      <span className="text-muted tinyFont">
                        {note.createdAt ? moment(note.createdAt).format('MMM D, YYYY h:mm A') : ''}
                      </span>
                    </div>
                    <p className="mb-1 xsmallFont text-dark">{note.text}</p>
                    {note.source && (
                      <span className="badge badge-secondary xtinyFont">
                        via {note.source}
                      </span>
                    )}
                  </Card.Body>
                </Card>
              );
            })
          )}
        </div>

        {canAddNote && (
          <div className="border-top pt-3 mt-2 d-flex align-items-end">
            <Form.Control
              as="textarea"
              rows={3}
              className="xsmallFont"
              value={text}
              onChange={e => setText(e.target.value)}
              placeholder="Add a billing note..."
              disabled={submitting}
            />
            <button
              className="btn btn-success xsmallFont ml-2 flex-shrink-0 d-flex align-items-center"
              onClick={handleAdd}
              disabled={!text.trim() || submitting}
            >
              {submitting ? (
                <Spinner animation="border" size="sm" className="mr-1 flex-shrink-0" />
              ) : (
                <Plus size={16} className="flex-shrink-0 mr-1" />
              )}
              Add
            </button>
          </div>
        )}
      </div>

      <ClientViewConfirmModal
        show={showConfirm}
        onHide={() => setShowConfirm(false)}
        onConfirm={async () => {
          setShowConfirm(false);
          await performAdd();
        }}
        staffName={staffName || 'Staff'}
        clientName={clientName || organizationName || 'Client'}
        title="Confirm billing note"
        submitting={submitting}
      />
    </SlidingSideBar>
  );
};

export default BillingNotesSheet;
